import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import FileNamesDisplay from './FileNamesDisplay';

// New data structure interfaces
interface UpuRemunerationGroupDto {
  id: number;
  name: string;
  displayName: string;
  operatorId: string | null;
}

interface RemunerationData {
  upuRemunerationGroupDto: UpuRemunerationGroupDto;
  mailFormat: string;
  mailCategory: string;
  grossSampledFlow: number;
  grossPercent: number;
  totalGrossSampledFlow: number;
  grossNonSampledFlow: number | null;
  nonExptIBRS: number | null;
  ibWeight: number | null;
  totalKg: number;
  rateKg: number;
  tdKg: number;
  ipk: number;
  itemsSampledFlow: number;
  itemsNonSampledFlow: number | null;
  totalItems: number;
  rateItem: number;
  tdItem: number;
  hasE53Data: boolean;
  notSubjToSampling: boolean;
  // Formatted fields
  totalGrossSampledFlowFormatted: string;
  grossPercentFormatted: string;
  formatFormatted: string;
  mailCategoryFormatted: string;
  grossSampledFlowFormatted: string;
  grossNonSampledFlowFormatted: string;
  remunerationGroupFormatted: string;
  nonExptIbrsFormatted: string;
  ibWeightFormatted: string;
  totalKgFormatted: string;
  rateKgFormatted: string;
  tdKgFormatted: string;
  ipkFormatted: string;
  itemsSampledFlowFormatted: string;
  itemsNonSampledFlowFormatted: string;
  totalItemsFormatted: string;
  rateItemFormatted: string;
  tdItemFormatted: string;
}

interface TotalData {
  totalKg: number;
  rateKg: number;
  tdKg: number;
  totalItems: number;
  rateItem: number;
  tdItem: number;
  totalKgFormatted: string;
  rateKgFormatted: string;
  tdKgFormatted: string;
  totalItemsFormatted: string;
  rateItemFormatted: string;
  tdItemFormatted: string;
}

interface PGEData {
  p: RemunerationData[];
  g: RemunerationData[];
  e: RemunerationData[];
  totalP: TotalData;
  totalG: TotalData;
  totalE: TotalData;
}

interface CN61CrrHtmlData {
  inbound: {
    pge: PGEData;
    fileRows: any | null;
  };
  outbound?: {
    pge: PGEData;
    fileRows: any | null;
  };
}

interface CN61CrrHtmlTableProps {
  data: CN61CrrHtmlData;
  title: string;
}

function CN61CrrHtmlTable({ data, title }: CN61CrrHtmlTableProps): React.ReactElement {
  if (!data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }} data-testid="cn61-crr-html-no-data">
        <Typography variant="body1" color="text.secondary" data-testid="cn61-crr-html-no-data-text">
          No CN61 CRR HTML data available
        </Typography>
      </Box>
    );
  }

  const renderRemunerationTableWithTotal = (data: RemunerationData[], totalData: TotalData, sectionTitle: string, isInbound: boolean) => {
    // Check if any row has hasE53Data: false
    const hasErrorRows = data.some(item => !item.hasE53Data);
    
    // Define background color based on inbound/outbound
    const cellBackgroundColor = isInbound ? '#e8f5e8' : '#ffeaea'; // Light green for inbound, light red for outbound
    
    // Style for numeric columns (right-aligned)
    const numericCellStyle = { textAlign: 'right' as const };
    // Style for text columns (center-aligned)
    const textCellStyle = { textAlign: 'center' as const };
    
    return (
      <Box sx={{ mb: 4 }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-section`}>
        <TableContainer component={Paper} sx={{ boxShadow: 1, mb: 2 }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-table-container`}>
          <Table size="small" data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-table`}>
            <TableHead data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-table-head`}>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-table-header-row`}>
                <TableCell sx={{ fontWeight: 600, ...textCellStyle }} data-testid="cn61-crr-html-remuneration-group-header">Remuneration Group</TableCell>
                <TableCell sx={{ fontWeight: 600, ...textCellStyle }} data-testid="cn61-crr-html-format-header">Format</TableCell>
                <TableCell sx={{ fontWeight: 600, ...textCellStyle }} data-testid="cn61-crr-html-mail-category-header">Mail Category</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-gross-sampled-flow-header">Gross (sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-gross-percent-header">Gross%</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-total-gross-sampled-flow-header">Total Gross (sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-gross-non-sampled-flow-header">Gross (non-sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-non-expt-ibrs-header">Non-expt-IBRS</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-ib-weight-header">IB Weight</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-total-kg-header">Total kg</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-rate-kg-header">Rate kg</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-td-kg-header">TD kg</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-ipk-header">IPK</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-items-sampled-flow-header">Items (sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-items-non-sampled-flow-header">Items (non-sampled flow)</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-total-items-header">Total items</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-rate-item-header">Rate item</TableCell>
                <TableCell sx={{ fontWeight: 600, ...numericCellStyle }} data-testid="cn61-crr-html-td-item-header">TD item</TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-table-body`}>
              {data.map((item, index) => (
                <TableRow key={index} sx={{ backgroundColor: cellBackgroundColor }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-table-row-${index}`}>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...textCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-remuneration-group-cell-${index}`}>{item.upuRemunerationGroupDto.displayName}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...textCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-format-cell-${index}`}>{item.formatFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...textCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-mail-category-cell-${index}`}>{item.mailCategoryFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-gross-sampled-flow-cell-${index}`}>{item.grossSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-gross-percent-cell-${index}`}>{item.grossPercentFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-gross-sampled-flow-cell-${index}`}>{item.totalGrossSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-gross-non-sampled-flow-cell-${index}`}>{item.grossNonSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-non-expt-ibrs-cell-${index}`}>{item.nonExptIbrsFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-ib-weight-cell-${index}`}>{item.ibWeightFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-kg-cell-${index}`}>{item.totalKgFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-rate-kg-cell-${index}`}>{item.rateKgFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-td-kg-cell-${index}`}>{item.tdKgFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-ipk-cell-${index}`}>{item.ipkFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-items-sampled-flow-cell-${index}`}>{item.itemsSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-items-non-sampled-flow-cell-${index}`}>{item.itemsNonSampledFlowFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-items-cell-${index}`}>{item.totalItemsFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-rate-item-cell-${index}`}>{item.rateItemFormatted}</TableCell>
                  <TableCell sx={{ backgroundColor: cellBackgroundColor, ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-td-item-cell-${index}`}>{item.tdItemFormatted}</TableCell>
                </TableRow>
              ))}
              {/* Total Row */}
              {totalData && (
                <TableRow sx={{ backgroundColor: '#e3f2fd', fontWeight: 600 }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-row`}>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...textCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-label`}>TOTAL</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...textCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...textCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-kg`}>{totalData.totalKgFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', backgroundColor: '#ffeb3b', ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-rate-kg`}>{totalData.rateKgFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-td-kg`}>{totalData.tdKgFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-items`}>{totalData.totalItemsFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', backgroundColor: '#ffeb3b', ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-rate-item`}>{totalData.rateItemFormatted}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontStyle: 'italic', ...numericCellStyle }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-total-td-item`}>{totalData.tdItemFormatted}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Error message for rows with hasE53Data: false */}
        {hasErrorRows && (
          <Box sx={{ mt: 1 }} data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-errors`}>
            {data
              .filter(item => !item.hasE53Data)
              .map((item, index) => (
                <Typography 
                  key={index}
                  variant="body2" 
                  sx={{ 
                    color: 'red', 
                    fontStyle: 'italic',
                    mb: 0.5
                  }}
                  data-testid={`cn61-crr-html-${sectionTitle.toLowerCase().replace(/\s+/g, '-')}-error-${index}`}
                >
                  No value for [AdjF/Net%/IPK] could be found for {item.remunerationGroupFormatted}, {item.formatFormatted}, {item.mailCategoryFormatted}. A substitution value has been used instead.
                </Typography>
              ))
            }
          </Box>
        )}
      </Box>
    );
  };


  return (
    <Box sx={{ width: '100%', mt: 4 }} data-testid="cn61-crr-html-table-container">
      {/* Report Header */}
      <Box sx={{ mb: 3 }} data-testid="cn61-crr-html-header">
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#063142', mb: 1 }} data-testid="cn61-crr-html-title">
          {title}
        </Typography>
      </Box>
      {/* File Names Display */}
      {data.inbound?.fileRows && (
        <FileNamesDisplay fileRows={data.inbound.fileRows} direction="inbound" />
      )}

      {/* Inbound Data */}
      {data.inbound && (
        <Box sx={{ mb: 4 }} data-testid="cn61-crr-html-inbound-section">
          {/* P Data */}
          {data.inbound.pge?.p && data.inbound.pge.p.length > 0 && (
            renderRemunerationTableWithTotal(data.inbound.pge.p, data.inbound.pge.totalP, 'P Format Data', true)
          )}

          {/* G Data */}
          {data.inbound.pge?.g && data.inbound.pge.g.length > 0 && (
            renderRemunerationTableWithTotal(data.inbound.pge.g, data.inbound.pge.totalG, 'G Format Data', true)
          )}

          {/* E Data */}
          {data.inbound.pge?.e && data.inbound.pge.e.length > 0 && (
            renderRemunerationTableWithTotal(data.inbound.pge.e, data.inbound.pge.totalE, 'E Format Data', true)
          )}
        </Box>
      )}
      {/* File Names Display */}
      {data.outbound?.fileRows && (
        <FileNamesDisplay fileRows={data.outbound.fileRows} direction="outbound" />
      )}

      {/* Outbound Data */}
      {data.outbound && (
        <Box sx={{ mb: 4 }} data-testid="cn61-crr-html-outbound-section">
          {/* P Data */}
          {data.outbound.pge?.p && data.outbound.pge.p.length > 0 && (
            renderRemunerationTableWithTotal(data.outbound.pge.p, data.outbound.pge.totalP, 'P Format Data', false)
          )}

          {/* G Data */}
          {data.outbound.pge?.g && data.outbound.pge.g.length > 0 && (
            renderRemunerationTableWithTotal(data.outbound.pge.g, data.outbound.pge.totalG, 'G Format Data', false)
          )}

          {/* E Data */}
          {data.outbound.pge?.e && data.outbound.pge.e.length > 0 && (
            renderRemunerationTableWithTotal(data.outbound.pge.e, data.outbound.pge.totalE, 'E Format Data', false)
          )}
        </Box>
      )}
    </Box>
  );
}

export default CN61CrrHtmlTable;
